import { useMemo, useRef, useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import type { ModelFacets } from "../types/model";
import type { ReleaseFilters } from "../lib/query-models";
import {
  type DateSegments,
  EMPTY_DATE_SEGMENTS,
  segmentsFromIso,
  segmentsToIso
} from "../lib/date-input";
import { DateField } from "./DateField";
import { SmoothScroll } from "./SmoothScroll";
import { useI18n } from "../hooks/use-i18n";
import { localizeFacetValue } from "../lib/localization";
import type { OrganizationIndex } from "../lib/organizations";

export type FacetKey =
  | "companies"
  | "categories"
  | "capabilities"
  | "statuses"
  | "openness";

export interface FacetCounts {
  companies: Map<string, number>;
  categories: Map<string, number>;
  capabilities: Map<string, number>;
  statuses: Map<string, number>;
  openness: Map<string, number>;
}

interface FacetPickerProps {
  facet: FacetKey;
  title: string;
  values: string[];
  selected: string[];
  counts: Map<string, number>;
  onToggle: (value: string) => void;
  searchable?: boolean;
  organizations: OrganizationIndex;
}

function FacetPicker({
  facet,
  title,
  values,
  selected,
  counts,
  onToggle,
  organizations,
  searchable = false
}: FacetPickerProps) {
  const { language, t } = useI18n();
  const [query, setQuery] = useState("");
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsContentRef = useRef<HTMLDivElement>(null);
  const matched = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("en");
    const filtered = normalized
      ? values.filter((value) => {
          const organization = facet === "companies" ? organizations.get(value) : undefined;
          return `${value} ${localizeFacetValue(facet, value, language, organizations)} ${organization?.names.en ?? ""} ${organization?.names.zh ?? ""} ${(organization?.aliases ?? []).join(" ")}`
            .toLocaleLowerCase(language === "zh" ? "zh-CN" : "en")
            .includes(normalized)
        })
      : values;
    return filtered;
  }, [facet, language, organizations, query, values]);
  const visible = matched.slice(0, 80);

  return (
    <section className="facet-picker">
      <h3>{title}</h3>
      {searchable ? (
        <label className="facet-search">
          <Search size={12} aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`${t("find")}${language === "zh" ? "" : " "}${title}…`}
            aria-label={`${t("find")}${language === "zh" ? "" : " "}${title}`}
          />
        </label>
      ) : null}
      <div ref={optionsRef} className="facet-options">
        <div ref={optionsContentRef} className="facet-options__content">
          {visible.map((value) => {
            const checked = selected.includes(value);
            return (
              <label
                className="facet-option"
                key={value}
                // On the row, not the name: the hidden checkbox covers the
                // row and would hide a title on the name from the pointer.
                title={localizeFacetValue(facet, value, language, organizations)}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(value)}
                />
                <i aria-hidden="true" />
                <span>
                  {localizeFacetValue(facet, value, language, organizations)}
                </span>
                <small>{counts.get(value) ?? 0}</small>
              </label>
            );
          })}
        </div>
      </div>
      <SmoothScroll
        wrapperRef={optionsRef}
        contentRef={optionsContentRef}
      />
      {/* Counted against the matches, so the hint appears only when the list
          is cut short and never asks a search to narrow a list it already
          narrowed to a few options. */}
      {visible.length < matched.length ? (
        <p className="facet-picker__hint">
          {t("showingFacet")} {visible.length} {t("of")} {matched.length}
          {language === "zh" ? "；" : "; "}
          {t("searchToNarrow")}
        </p>
      ) : query.trim() && matched.length === 0 ? (
        // A search that matches nothing says so rather than leaving a blank
        // column, in the hint's own words.
        <p className="facet-picker__hint">
          {t("showingFacet")} 0 {t("of")} {values.length}
        </p>
      ) : null}
    </section>
  );
}

interface FilterPanelProps {
  open: boolean;
  facets: ModelFacets;
  filters: ReleaseFilters;
  counts: FacetCounts;
  onToggle: (key: FacetKey, value: string) => void;
  onDates: (from: string, to: string) => void;
  onReset: () => void;
  onClose: () => void;
  organizations: OrganizationIndex;
}

export function FilterPanel({
  open,
  facets,
  filters,
  counts,
  onToggle,
  onDates,
  onReset,
  onClose,
  organizations
}: FilterPanelProps) {
  const { t } = useI18n();
  const [activeDate, setActiveDate] = useState<"from" | "to" | null>(null);
  const [dateDrafts, setDateDrafts] = useState(() => ({
    from: segmentsFromIso(filters.from),
    to: segmentsFromIso(filters.to)
  }));

  if (!open) return null;

  const updateDate = (key: "from" | "to", segments: DateSegments) => {
    setDateDrafts((current) => ({ ...current, [key]: segments }));
    const value = segmentsToIso(segments) ?? "";
    if (key === "from") onDates(value, filters.to);
    else onDates(filters.from, value);
  };

  return (
    <div
      className="filter-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="filter-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filters-title"
        onMouseDown={(event) => {
          if (
            activeDate &&
            !(event.target as HTMLElement).closest(".date-field")
          ) {
            setActiveDate(null);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && activeDate) {
            event.stopPropagation();
            setActiveDate(null);
          }
        }}
      >
        <header className="filter-panel__header">
          <div>
            <h2 id="filters-title">{t("filters")}</h2>
            <p>{t("filterDescription")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X size={15} aria-hidden="true" />
            <span className="sr-only">{t("closeFilters")}</span>
          </button>
        </header>

        <div className="facet-grid">
          <FacetPicker
            facet="companies"
            title={t("company")}
            values={facets.companies}
            selected={filters.companies}
            counts={counts.companies}
            organizations={organizations}
            onToggle={(value) => onToggle("companies", value)}
            searchable
          />
          <FacetPicker
            facet="categories"
            title={t("category")}
            values={facets.categories}
            selected={filters.categories}
            counts={counts.categories}
            organizations={organizations}
            onToggle={(value) => onToggle("categories", value)}
          />
          <FacetPicker
            facet="capabilities"
            title={t("capability")}
            values={facets.capabilities}
            selected={filters.capabilities}
            counts={counts.capabilities}
            organizations={organizations}
            onToggle={(value) => onToggle("capabilities", value)}
            searchable
          />
          <FacetPicker
            facet="statuses"
            title={t("releaseStatus")}
            values={facets.statuses}
            selected={filters.statuses}
            counts={counts.statuses}
            organizations={organizations}
            onToggle={(value) => onToggle("statuses", value)}
          />
          <FacetPicker
            facet="openness"
            title={t("openness")}
            values={facets.openness}
            selected={filters.openness}
            counts={counts.openness}
            organizations={organizations}
            onToggle={(value) => onToggle("openness", value)}
          />
        </div>

        <footer className="filter-panel__footer">
          <div className="date-range">
            <span>{t("releaseDate")}</span>
            <DateField
              label="From"
              segments={dateDrafts.from}
              open={activeDate === "from"}
              onOpen={() => setActiveDate("from")}
              onClose={() => setActiveDate(null)}
              onSegments={(segments) => updateDate("from", segments)}
            />
            <ArrowRight
              className="date-range__arrow"
              size={12}
              aria-hidden="true"
            />
            <DateField
              label="To"
              segments={dateDrafts.to}
              open={activeDate === "to"}
              onOpen={() => setActiveDate("to")}
              onClose={() => setActiveDate(null)}
              onSegments={(segments) => updateDate("to", segments)}
            />
          </div>
          <div className="filter-panel__actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setDateDrafts({
                  from: { ...EMPTY_DATE_SEGMENTS },
                  to: { ...EMPTY_DATE_SEGMENTS }
                });
                setActiveDate(null);
                onReset();
              }}
            >
              <span>{t("reset")}</span>
            </button>
            <button type="button" className="primary-button" onClick={onClose}>
              <span>{t("done")}</span>
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
